/**
 * Search Query Analyzer
 * Detects query patterns and suggests optimal search strategy
 */

export interface QueryAnalysis {
  query: string;
  shouldUseEnhanced: boolean;
  reason?: string;
  patterns: {
    isComplex: boolean;
    hasActorPattern: boolean;
    hasDirectorPattern: boolean;
    hasGenrePattern: boolean;
    hasYearPattern: boolean;
    hasNonEnglish: boolean;
    hasMultipleKeywords: boolean;
    /** e.g. "Season Mohanlal" — title + person/language; basic TMDB search is a poor match */
    hasTwoWordTitleContext: boolean;
  };
  confidence: number; // 0-1 scale
}

/**
 * Analyze search query and determine if enhanced search should be used
 */
export function analyzeSearchQuery(query: string): QueryAnalysis {
  const trimmed = query.trim().toLowerCase();

  const patterns = {
    isComplex: false,
    hasActorPattern: false,
    hasDirectorPattern: false,
    hasGenrePattern: false,
    hasYearPattern: false,
    hasNonEnglish: false,
    hasMultipleKeywords: false,
    hasTwoWordTitleContext: false,
  };

  let shouldUseEnhanced = false;
  let reason: string | undefined;
  let confidence = 0;

  // Pattern 1: Actor/Director patterns
  const actorKeywords = ['starring', 'with', 'actor', 'actress', 'cast'];
  const directorKeywords = ['directed by', 'director', 'by'];

  patterns.hasActorPattern = actorKeywords.some(keyword => trimmed.includes(keyword));
  patterns.hasDirectorPattern = directorKeywords.some(keyword => trimmed.includes(keyword));

  if (patterns.hasActorPattern || patterns.hasDirectorPattern) {
    patterns.isComplex = true;
    shouldUseEnhanced = true;
    reason = patterns.hasActorPattern
      ? 'Query mentions actor/cast - enhanced search can find movies by actor'
      : 'Query mentions director - enhanced search can find movies by director';
    confidence = 0.9;
  }

  // Pattern 2: Genre patterns
  const genres = [
    'action', 'adventure', 'animation', 'comedy', 'crime', 'documentary',
    'drama', 'family', 'fantasy', 'history', 'horror', 'music', 'mystery',
    'romance', 'sci-fi', 'science fiction', 'thriller', 'war', 'western'
  ];

  patterns.hasGenrePattern = genres.some(genre => trimmed.includes(genre));

  if (patterns.hasGenrePattern && trimmed.split(/\s+/).length > 2) {
    patterns.isComplex = true;
    shouldUseEnhanced = true;
    reason = 'Query includes genre with other terms - enhanced search can filter by genre';
    confidence = 0.85;
  }

  // Pattern 3: Year patterns
  const yearMatch = trimmed.match(/\b(19\d{2}|20\d{2})\b/);
  patterns.hasYearPattern = !!yearMatch;

  if (patterns.hasYearPattern && trimmed.split(/\s+/).length > 2) {
    patterns.isComplex = true;
    shouldUseEnhanced = true;
    reason = `Query includes year (${yearMatch![0]}) - enhanced search can filter by release year`;
    confidence = 0.8;
  }

  // Pattern 4: Non-English characters (Unicode)
  const nonEnglishRegex = /[^\u0000-\u007F]/;
  patterns.hasNonEnglish = nonEnglishRegex.test(trimmed);

  if (patterns.hasNonEnglish) {
    patterns.isComplex = true;
    shouldUseEnhanced = true;
    reason = 'Query contains non-English characters - enhanced search supports multiple languages';
    confidence = 0.95;
  }

  // Pattern 4b: Regional language name in the query (e.g. "Season malayalam", "Amen hindi")
  // Basic TMDB search treats the whole string as a title; enhanced search strips language and boosts by original_language.
  const regionalLanguageMarkers = [
    'malayalam', 'hindi', 'tamil', 'telugu', 'kannada', 'bengali', 'marathi',
    'gujarati', 'punjabi', 'korean', 'japanese', 'chinese', 'russian', 'arabic',
    'spanish', 'french', 'german', 'italian', 'portuguese'
  ];

  if (!shouldUseEnhanced && regionalLanguageMarkers.some(lang => trimmed.includes(lang))) {
    patterns.isComplex = true;
    shouldUseEnhanced = true;
    reason =
      'Query includes a regional language — enhanced search matches titles in that language';
    confidence = 0.9;
  }

  // Pattern 4c: Exactly two tokens (e.g. "Season Padmarajan", "Amen Malayalam" without hitting 4b first)
  // Matches server + client routing that treats these as title + disambiguator, not a single TMDB title string.
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  if (
    !shouldUseEnhanced &&
    tokens.length === 2 &&
    tokens[0].length >= 2 &&
    tokens[1].length >= 2
  ) {
    patterns.hasTwoWordTitleContext = true;
    patterns.isComplex = true;
    shouldUseEnhanced = true;
    reason =
      'Two-word query — likely a title plus actor, director, or language; enhanced search disambiguates';
    confidence = 0.88;
  }

  // Pattern 5: Multiple keywords (4+ words)
  const wordCount = trimmed.split(/\s+/).filter(w => w.length > 2).length;
  patterns.hasMultipleKeywords = wordCount >= 4;

  if (patterns.hasMultipleKeywords && !shouldUseEnhanced) {
    patterns.isComplex = true;
    shouldUseEnhanced = true;
    reason = `Query has ${wordCount} keywords - enhanced search can match across multiple fields`;
    confidence = 0.7;
  }

  return {
    query,
    shouldUseEnhanced,
    reason,
    patterns,
    confidence,
  };
}

/**
 * Check if query looks like it needs spelling correction
 */
export function needsSpellingCorrection(query: string, resultCount: number): boolean {
  // Suggest spelling correction if:
  // 1. Zero results
  // 2. Query is reasonably long (3+ characters)
  // 3. Query doesn't look like intentional gibberish

  const trimmed = query.trim();

  if (resultCount > 0) return false;
  if (trimmed.length < 3) return false;

  // Check if it's all numbers or special characters (probably intentional)
  const hasLetters = /[a-zA-Z]/.test(trimmed);
  if (!hasLetters) return false;

  // Check if it has reasonable word structure
  const words = trimmed.split(/\s+/);
  const hasReasonableWords = words.some(w => w.length >= 3);

  return hasReasonableWords;
}

/**
 * Suggest enhanced search based on zero results
 */
export function shouldSuggestEnhancedForZeroResults(
  query: string,
  resultCount: number,
  currentlyUsingEnhanced: boolean
): { shouldSuggest: boolean; reason?: string } {
  // Don't suggest if already using enhanced
  if (currentlyUsingEnhanced) {
    return { shouldSuggest: false };
  }

  // Don't suggest for very short queries
  if (query.trim().length < 3) {
    return { shouldSuggest: false };
  }

  // Suggest enhanced if zero results
  if (resultCount === 0) {
    return {
      shouldSuggest: true,
      reason: 'No results found - enhanced search can find movies by actor, director, or partial matches',
    };
  }

  return { shouldSuggest: false };
}

/**
 * Auto-upgrade search based on analysis
 * Returns true if search should automatically use enhanced mode
 */
export function shouldAutoUpgradeToEnhanced(analysis: QueryAnalysis): boolean {
  // Auto-upgrade if high confidence complex query
  return analysis.shouldUseEnhanced && analysis.confidence >= 0.85;
}

/**
 * Get user-friendly explanation for why enhanced search is recommended
 */
export function getEnhancedSearchExplanation(analysis: QueryAnalysis): string {
  if (!analysis.shouldUseEnhanced || !analysis.reason) {
    return 'Enhanced search uses multiple strategies to find better matches';
  }

  return analysis.reason;
}
